// src/pages/about/changelog/add-changelog/AddChangelog.jsx
import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Grid,
    Paper,
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Button,
    Stack,
    Alert,
    Dialog as AlertDialog,
    DialogContent as AlertDialogContent,
    DialogTitle as AlertDialogTitle,
    DialogActions,
} from "@mui/material";
import { Slide } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Import Tiptap Editor
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';

// Import ToolbarControl
import ToolbarControl from './ToolbarControl';

// Lowlight untuk syntax highlighting
const lowlight = createLowlight(common);

// Transition untuk slide in dialog
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Fungsi untuk menghitung karakter dari HTML
const countCharactersFromHTML = (html) => {
    if (!html) return 0;
    // Hapus tag HTML dan hitung karakter
    const text = html.replace(/<[^>]*>/g, '');
    return text.length;
};

function AddChangelog({ 
    open, 
    onClose, 
    formData, 
    onFormChange, 
    onSave,
    snackbar,
    onCloseSnackbar
}) {
    // State untuk alert dialog insert link/image
    const [alertDialog, setAlertDialog] = useState({
        open: false,
        type: '', // 'link' atau 'image'
        title: '',
        label: '',
        value: '',
    });

    // Tiptap Editor
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4],
                },
                codeBlock: false,
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'tiptap-link',
                    rel: 'noopener noreferrer',
                    target: '_blank',
                },
            }),
            Image.configure({
                HTMLAttributes: {
                    class: 'tiptap-image',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
                alignments: ['left', 'center', 'right', 'justify'],
            }),
            Highlight.configure({
                multicolor: true,
            }),
            CodeBlockLowlight.configure({
                lowlight,
            }),
            Placeholder.configure({
                placeholder: 'Masukkan deskripsi changelog di sini...',
            }),
        ],
        content: formData.description,
        onUpdate: ({ editor }) => {
            onFormChange('description', editor.getHTML());
        },
    });

    // Update editor content ketika formData.description berubah
    React.useEffect(() => {
        if (editor && formData.description !== editor.getHTML()) {
            editor.commands.setContent(formData.description);
        }
    }, [formData.description, editor]);

    // Hitung karakter dari HTML
    const characterCount = countCharactersFromHTML(formData.description);

    const handleClose = () => {
        // Reset editor
        if (editor) {
            editor.commands.clearContent();
        }
        onClose();
    };

    // Fungsi untuk membuka alert dialog insert link/image
    const openAlertDialog = (type) => {
        const previousValue = editor.getAttributes('link').href || '';
        setAlertDialog({
            open: true,
            type,
            title: type === 'link' ? 'Insert Link' : 'Insert Image',
            label: type === 'link' ? 'Masukkan URL' : 'Masukkan URL gambar',
            value: previousValue,
        });
    };

    // Fungsi untuk menutup alert dialog
    const closeAlertDialog = () => {
        setAlertDialog({
            open: false,
            type: '',
            title: '',
            label: '',
            value: '',
        });
    };

    // Fungsi untuk menyimpan link/image dari alert dialog
    const saveAlertDialog = () => {
        const { type, value } = alertDialog;
        
        if (value.trim() === '') {
            if (type === 'link') {
                editor.chain().focus().unsetLink().run();
            }
            closeAlertDialog();
            return;
        }

        if (type === 'link') {
            editor.chain().focus().setLink({ href: value }).run();
        } else if (type === 'image') {
            editor.chain().focus().setImage({ src: value }).run();
        }

        closeAlertDialog();
    };

    // Custom Toolbar Control dengan alert dialog
    const CustomToolbarControl = () => {
        if (!editor) {
            return null;
        }

        return (
            <ToolbarControl 
                editor={editor} 
                onInsertLink={() => openAlertDialog('link')}
                onInsertImage={() => openAlertDialog('image')}
            />
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            fullScreen
            maxWidth="lg"
        >
            <DialogTitle sx={{ 
                m: 0, 
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography variant="h6" component="div">
                    Tambah Changelog Baru
                </Typography>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ backgroundColor:'#f5f5f5', p: 3 }}>
                <Grid container spacing={2} sx={{ width: '100%', mt:2 }}>
                    {/* Bagian kiri: xs:6 md:9 */}
                    <Grid size={{ xs: 6, md: 9 }}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    fullWidth
                                    label="Judul Changelog"
                                    value={formData.title}
                                    onChange={(e) => onFormChange('title', e.target.value)}
                                    placeholder="Masukkan judul changelog"
                                    variant="outlined"
                                    size="medium"
                                />
                            </Box>
                            
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
                                    Deskripsi
                                </Typography>
                                
                                {/* Tiptap Toolbar */}
                                <CustomToolbarControl />
                                
                                {/* Tiptap Editor */}
                                <Paper 
                                    elevation={0}
                                    sx={{ 
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        minHeight: 300,
                                        backgroundColor: 'white',
                                        overflow: 'auto',
                                        p: 2,
                                        '& .tiptap': {
                                            minHeight: 250,
                                            outline: 'none',
                                            '& h1': {
                                                fontSize: '2em',
                                                fontWeight: 'bold',
                                                margin: '1em 0',
                                            },
                                            '& h2': {
                                                fontSize: '1.5em',
                                                fontWeight: 'bold',
                                                margin: '0.83em 0',
                                            },
                                            '& h3': {
                                                fontSize: '1.17em',
                                                fontWeight: 'bold',
                                                margin: '1em 0',
                                            },
                                            '& h4': {
                                                fontSize: '1em',
                                                fontWeight: 'bold',
                                                margin: '1.33em 0',
                                            },
                                            '& p': {
                                                margin: '0.5em 0',
                                            },
                                            '& ul, & ol': {
                                                paddingLeft: '1.5em',
                                                margin: '0.5em 0',
                                            },
                                            '& blockquote': {
                                                borderLeft: '3px solid #ccc',
                                                margin: '0.5em 0',
                                                paddingLeft: '1em',
                                                fontStyle: 'italic',
                                            },
                                            '& code': {
                                                backgroundColor: '#f5f5f5',
                                                padding: '0.2em 0.4em',
                                                borderRadius: '3px',
                                                fontFamily: 'monospace',
                                            },
                                            '& pre': {
                                                backgroundColor: '#f5f5f5',
                                                padding: '1em',
                                                borderRadius: '5px',
                                                overflow: 'auto',
                                            },
                                            '& .tiptap-image': {
                                                maxWidth: '100%',
                                                height: 'auto',
                                                borderRadius: '4px',
                                            },
                                            '& .tiptap-link': {
                                                color: '#1976d2',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                            },
                                            '& mark': {
                                                backgroundColor: '#fff3cd',
                                                padding: '0.1em 0.2em',
                                                borderRadius: '2px',
                                            },
                                        }
                                    }}
                                >
                                    <EditorContent editor={editor} />
                                </Paper>
                                
                                <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {characterCount} karakter
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Gunakan toolbar di atas untuk memformat teks
                                    </Typography>
                                </Stack>
                            </Box>
                        </Paper>
                    </Grid>
                    
                    {/* Bagian kanan: xs:6 md:3 */}
                    <Grid size={{ xs: 6, md: 3 }}>
                        <Paper sx={{ p: 3, height: '100%' }}>
                            <Stack spacing={3}>
                                {/* Kategori Dropdown */}
                                <FormControl fullWidth>
                                    <InputLabel id="category-label">Kategori</InputLabel>
                                    <Select
                                        labelId="category-label"
                                        value={formData.category}
                                        label="Kategori"
                                        onChange={(e) => onFormChange('category', e.target.value)}
                                    >
                                        <MenuItem value="New Feature">New Feature</MenuItem>
                                        <MenuItem value="Update">Update</MenuItem>
                                        <MenuItem value="Bug Fix">Bug Fix</MenuItem>
                                        <MenuItem value="Improvement">Improvement</MenuItem>
                                    </Select>
                                </FormControl>
                                
                                {/* Tanggal dan Waktu */}
                                <TextField
                                    fullWidth
                                    label="Tanggal & Waktu"
                                    type="datetime-local"
                                    value={formData.datetime}
                                    onChange={(e) => onFormChange('datetime', e.target.value)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CalendarTodayIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                
                                {/* Status Dropdown */}
                                <FormControl fullWidth>
                                    <InputLabel id="status-label">Status</InputLabel>
                                    <Select
                                        labelId="status-label"
                                        value={formData.status}
                                        label="Status"
                                        onChange={(e) => onFormChange('status', e.target.value)}
                                    >
                                        <MenuItem value="Draft">Draft</MenuItem>
                                        <MenuItem value="Published">Published</MenuItem>
                                    </Select>
                                </FormControl>
                                
                                {/* Tombol Simpan */}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    fullWidth
                                    startIcon={<SaveIcon />}
                                    onClick={onSave}
                                    sx={{ mt: 2 }}
                                >
                                    Simpan Changelog
                                </Button>
                                
                                {/* Tombol Batal */}
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    size="medium"
                                    fullWidth
                                    onClick={handleClose}
                                >
                                    Batal
                                </Button>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            {/* Alert Dialog untuk Insert Link/Image */}
            <AlertDialog
                open={alertDialog.open}
                onClose={closeAlertDialog}
                TransitionComponent={Transition}
                maxWidth="sm"
                fullWidth
            >
                <AlertDialogTitle>{alertDialog.title}</AlertDialogTitle>
                <AlertDialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={alertDialog.label}
                        type="url"
                        fullWidth
                        variant="outlined"
                        value={alertDialog.value}
                        onChange={(e) => setAlertDialog(prev => ({ ...prev, value: e.target.value }))}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                saveAlertDialog();
                            }
                        }}
                    />
                </AlertDialogContent>
                <DialogActions>
                    <Button onClick={closeAlertDialog}>Batal</Button>
                    <Button onClick={saveAlertDialog} variant="contained" color="primary">
                        Simpan
                    </Button>
                </DialogActions>
            </AlertDialog>
        </Dialog>
    );
}

export default AddChangelog;